/**
 * What a developer actually sells, and what to call it.
 *
 * **It is not only plots.** A developer sells land, houses of a dozen kinds, warehouses, shops and
 * offices — and sometimes **a whole block of flats as one item**, a building with twelve flats
 * inside it bought by one person. The first version of this module called every one of those a
 * "plot", which is right for a bare piece of land and wrong for everything else.
 *
 * Two things follow from that, and they are separate:
 *
 * 1. **The thing sold has a *kind*.** Land, detached house, terrace, block of flats, warehouse.
 *    A real list rather than free text, because it is what a buyer asks first and what a developer
 *    filters by — and free text cannot be filtered on.
 * 2. **Some kinds contain others.** A block of flats is one sale and twelve flats. So the thing
 *    sold carries how many units are inside it, and it is 1 for a plot of land and for a house.
 *
 * And the word itself has to follow the estate. A scheme selling bare land says "plots"; one
 * selling houses says "homes"; one selling shop units says "units". Calling all three "plots" is
 * how a product reads as having been built for somebody else's business. ADR-0081.
 */

import { addDays, type PlainDate } from "./dates";
import { applyRate, type BasisPoints } from "./money";

export const SELLS = ["land", "homes", "commercial", "mixed"] as const;
export type Sells = (typeof SELLS)[number];

export interface SellsInfo {
  value: Sells;
  label: string;
  /** What one of the things in it is called, and what many are called. */
  noun: { one: string; many: string };
  hint: string;
}

export const SELLS_INFO: Record<Sells, SellsInfo> = {
  land: {
    value: "land",
    label: "Land",
    noun: { one: "plot", many: "plots" },
    hint: "Bare or serviced land, sold by the plot.",
  },
  homes: {
    value: "homes",
    label: "Homes",
    noun: { one: "home", many: "homes" },
    hint: "Houses, duplexes and flats, built or being built.",
  },
  commercial: {
    value: "commercial",
    label: "Commercial",
    noun: { one: "unit", many: "units" },
    hint: "Shops, offices and warehouses.",
  },
  mixed: {
    value: "mixed",
    label: "A bit of everything",
    noun: { one: "unit", many: "units" },
    hint: "Land, homes and commercial in the same scheme.",
  },
};

/**
 * What to call the things in this estate.
 *
 * The one function every screen in the developer module asks before writing a heading. An unknown
 * or missing value reads as `mixed`, whose noun is "unit" — the only one of the four that is not
 * wrong for any of the others.
 */
export function soldNoun(sells?: string | null): { one: string; many: string } {
  return (SELLS_INFO[sells as Sells] ?? SELLS_INFO.mixed).noun;
}

/* ------------------------------------------------------------------- kinds */

export const PLOT_KINDS = [
  "land",
  "detached",
  "semi_detached",
  "terrace",
  "duplex",
  "bungalow",
  "maisonette",
  "flat",
  "block_of_flats",
  "shop",
  "office",
  "warehouse",
  "mixed_use",
  "other",
] as const;

export type PlotKind = (typeof PLOT_KINDS)[number];

export interface PlotKindInfo {
  value: PlotKind;
  label: string;
  /**
   * True when one of these contains several lettable or sellable units.
   *
   * A block of flats is one sale and twelve flats; a mixed-use building is one sale and a row of
   * shops with offices above. Everything else is one thing. The count is asked for only where this
   * is true, because asking "how many units?" about a plot of land is a question with one answer
   * and no purpose.
   */
  holdsUnits: boolean;
  /** Which kinds of estate offer it, so a land scheme is not asked about warehouses. */
  sells: Sells[];
}

export const PLOT_KIND_INFO: Record<PlotKind, PlotKindInfo> = {
  land: { value: "land", label: "Plot of land", holdsUnits: false, sells: ["land", "mixed"] },
  detached: { value: "detached", label: "Detached house", holdsUnits: false, sells: ["homes", "mixed"] },
  semi_detached: { value: "semi_detached", label: "Semi-detached house", holdsUnits: false, sells: ["homes", "mixed"] },
  terrace: { value: "terrace", label: "Terrace", holdsUnits: false, sells: ["homes", "mixed"] },
  duplex: { value: "duplex", label: "Duplex", holdsUnits: false, sells: ["homes", "mixed"] },
  bungalow: { value: "bungalow", label: "Bungalow", holdsUnits: false, sells: ["homes", "mixed"] },
  maisonette: { value: "maisonette", label: "Maisonette", holdsUnits: false, sells: ["homes", "mixed"] },
  flat: { value: "flat", label: "Flat", holdsUnits: false, sells: ["homes", "mixed"] },
  block_of_flats: {
    value: "block_of_flats",
    label: "Block of flats",
    holdsUnits: true,
    sells: ["homes", "commercial", "mixed"],
  },
  shop: { value: "shop", label: "Shop", holdsUnits: false, sells: ["commercial", "mixed"] },
  office: { value: "office", label: "Office", holdsUnits: false, sells: ["commercial", "mixed"] },
  warehouse: { value: "warehouse", label: "Warehouse", holdsUnits: false, sells: ["commercial", "mixed"] },
  mixed_use: { value: "mixed_use", label: "Mixed-use building", holdsUnits: true, sells: ["commercial", "mixed"] },
  other: { value: "other", label: "Something else", holdsUnits: true, sells: ["land", "homes", "commercial", "mixed"] },
};

export function isPlotKind(value: unknown): value is PlotKind {
  return typeof value === "string" && (PLOT_KINDS as readonly string[]).includes(value);
}

/** What this estate is offered. Everything, when the estate has not said. */
export function kindsFor(sells?: string | null): PlotKindInfo[] {
  const which = (SELLS_INFO[sells as Sells] ?? SELLS_INFO.mixed).value;
  return PLOT_KINDS.map((kind) => PLOT_KIND_INFO[kind]).filter((info) => info.sells.includes(which));
}

/**
 * How many units this thing holds.
 *
 * One, unless it is a kind that contains others and a number was given. Never zero: a building
 * that holds nothing is not a building anybody is selling, and a zero here would divide by itself
 * in every per-unit figure downstream.
 */
export function unitsIn(kind: string | null | undefined, units: number | null | undefined): number {
  if (!isPlotKind(kind) || !PLOT_KIND_INFO[kind].holdsUnits) return 1;
  return units && units > 0 ? Math.floor(units) : 1;
}

/**
 * How one of these reads on a row: what it is, and what is inside it.
 *
 * "Block of flats · 12 flats" rather than "block_of_flats", because the second is a column name
 * and the first is what somebody is looking for.
 */
export function describePlot(kind: string | null | undefined, units: number | null | undefined): string {
  if (!isPlotKind(kind)) return "—";
  const info = PLOT_KIND_INFO[kind];
  const held = unitsIn(kind, units);
  if (!info.holdsUnits || held <= 1) return info.label;
  return `${info.label} · ${held} ${kind === "block_of_flats" ? (held === 1 ? "flat" : "flats") : held === 1 ? "unit" : "units"}`;
}

/* ------------------------------------------------------------------- the tree */

/**
 * Property → Phase → Block → Unit, and why only two of the four are tables.
 *
 * ```
 * Property        Greenwich Heights          always          (estates)
 * └── Phase       Phase 1A                   optional        (phases)
 *     └── Block   Tower A, Townhouse Row 1   optional        (plots, sellable = false)
 *         └── Unit  Apt 402 (floor 4)        the thing sold  (plots, sellable = true)
 * ```
 *
 * **Phase and Block are both optional, and an estate with none behaves exactly as it always did.**
 * A land scheme is Property → Plot and never sees the middle two. A tower uses all four. Making
 * them mandatory would have every land developer maintaining two empty levels forever, which is the
 * shape this design was corrected away from.
 *
 * **A block is a `plots` row, not a table.** The thing being sold is not a level: one developer
 * sells Townhouse 3 whole, another splits Tower A into twelve flats, a third sells bare plots
 * beside both. Four tables would make "a townhouse sold whole" and "a tower containing flats" the
 * same table meaning two different things, force a second foreign key on every purchase, and make
 * splitting a sold building a migration between tables rather than an `UPDATE`. So: one table, one
 * tree, `parentId` nullable, `sellable` boolean. The long form is in `packages/db/src/schema.ts`
 * above the `plots` table.
 *
 * **Floor is not a level.** Nobody sells, prices, allocates or hands over a floor — it is a label
 * on the unit, and a level for it would mean rows nothing transacts against and a join on every
 * query for nothing.
 */

export const PHASE_STATUSES = ["planning", "selling", "sold_out", "closed"] as const;
export type PhaseStatus = (typeof PHASE_STATUSES)[number];

export const PHASE_STATUS_LABELS: Record<PhaseStatus, string> = {
  planning: "Planning",
  selling: "Selling",
  sold_out: "Sold out",
  closed: "Closed",
};

export function isPhaseStatus(value: unknown): value is PhaseStatus {
  return typeof value === "string" && (PHASE_STATUSES as readonly string[]).includes(value);
}

/**
 * How deep the tree may go, counted in `plots` rows.
 *
 * Two: a block, and the units inside it. The agreed hierarchy has exactly one grouping level, and a
 * tree that allows more is a tree somebody eventually builds Tower A → Floor 4 → Apt 402 in — which
 * is the thing the design says not to do, and which no screen, no statement and no offer letter is
 * written to render.
 *
 * It is one condition in one place, so relaxing it later is deleting a check rather than unpicking
 * an assumption.
 */
export const MAX_TREE_DEPTH = 2;

/**
 * Nest a flat list of nodes by `parentId`.
 *
 * Written here rather than in the endpoint because it is pure, because it is the one place the
 * shape of a tree is decided, and because the API and — later — the listing site have to agree
 * about it.
 *
 * **A node whose parent is not in the list becomes a root.** That is not a defensive flourish: a
 * caller reading one phase's nodes hands this a set whose parents may sit outside it, and silently
 * dropping those rows would show a developer a phase with nothing in it.
 */
export function buildTree<T extends { id: string; parentId: string | null }>(rows: T[]): Array<T & { children: T[] }> {
  type Built = T & { children: Built[] };
  const byId = new Map<string, Built>();
  for (const row of rows) byId.set(row.id, { ...(row as T), children: [] } as Built);

  const roots: Built[] = [];
  for (const row of rows) {
    const node = byId.get(row.id)!;
    const parent = row.parentId ? byId.get(row.parentId) : undefined;
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }
  return roots as Array<T & { children: T[] }>;
}

/**
 * Where this node sits, in the words a screen uses.
 *
 * There is no `level` column and there must not be one: a node's level is not a fact about the row,
 * it is a fact about the row's relationships, and a column would be a second copy of that fact
 * waiting to disagree with the first.
 *
 * **Position, not saleability.** A flat that has been taken off sale — which is what putting a
 * building back together does to the flats inside it — is still a unit; it is a unit nobody is
 * selling. An earlier version read `sellable` first and called every such flat a "block", which is
 * the one thing it is not: it groups nothing, and the tree is two deep so it never can.
 */
export function levelOf(node: { sellable: boolean; parentId?: string | null }): "block" | "unit" | "standalone" {
  if (node.parentId) return "unit";
  return node.sellable ? "standalone" : "block";
}

/* ----------------------------------------------------- the one hard refusal */

/**
 * The refusal when a sellable thing would contain a sellable thing.
 *
 * Tower A for sale *and* Apt 402 for sale is the same square metre sold to two people, and there is
 * no honest statement, offer letter or handover that can be produced from it. So it is refused
 * outright — the single hard refusal in the whole module.
 *
 * **The message names the fix, not the rule.** "A sellable node may not contain a sellable node" is
 * true, unactionable, and reads as the software being difficult; "Tower A is for sale. Mark it
 * unsellable before putting its units on sale." is the same refusal with the next step in it. The
 * shape was the user's.
 *
 * One function serves both directions, because the direction only decides which name goes where:
 * marking a child sellable names the parent, and marking a parent sellable names the child.
 */
export function nestedSaleMessage(forSale: string, wanted: string, others = 0): string {
  const subject = others > 0 ? `${forSale} and ${others} other${others === 1 ? "" : "s"}` : forSale;
  const verb = others > 0 ? "are" : "is";
  const them = others > 0 ? "them" : "it";
  return `${subject} ${verb} for sale. Mark ${them} unsellable before putting ${wanted} on sale.`;
}

/**
 * The refusal when a switch would leave a live sale pointing at nothing.
 *
 * **Switching whole ↔ split is allowed even after a sale.** The first proposal refused it and the
 * user overruled that, rightly: a developer who sold a townhouse, had the buyer default, and now
 * wants six flats out of it has an ordinary need. What is refused is a *silent* switch — one that
 * would leave a buyer's purchase attached to something no longer for sale without anybody having
 * said where that buyer lands.
 *
 * So the message asks, by name, the question the request failed to answer. "Yes, I'm sure" is not
 * an answer to it.
 */
export function orphanedSaleMessage(buyer: string, node: string): string {
  return `${buyer} has bought ${node}. Say which one that becomes, or cancel the sale, before changing how it is sold.`;
}

/* ----------------------------------------------------- price and commission */

/**
 * The asking price of a sellable thing: **its own, or its type's.**
 *
 * Price lives on the type — "500sqm corner · ₦18m" — so that a price rise across two hundred plots
 * is one edit rather than two hundred. But the corner plot that goes for more than the rest of its
 * type is an ordinary thing, so the node's own figure wins wherever it has one.
 *
 * `null` rather than `0` when neither has an answer. Zero is a price; "not priced yet" is not, and a
 * card reading "0" is worse than one reading "Price on application".
 *
 * **What this never touches is `purchases.price`.** A sale already agreed keeps the figure it was
 * agreed at; re-pricing a type moves what is asked of the next buyer and nothing else.
 */
export function resolveAskingPrice(
  node: { price?: number | null } | null | undefined,
  type?: { price?: number | null } | null,
): number | null {
  if (node?.price !== null && node?.price !== undefined) return node.price;
  if (type?.price !== null && type?.price !== undefined) return type.price;
  return null;
}

/** The four levels a rate can be set at, most specific first. One list, so two readers cannot disagree. */
export interface CommissionChain {
  node?: { commissionBp?: number | null } | null;
  type?: { commissionBp?: number | null } | null;
  phase?: { commissionBp?: number | null } | null;
  estate?: { commissionBp?: number | null } | null;
}

const COMMISSION_ORDER = ["node", "type", "phase", "estate"] as const;

/**
 * The commission rate that applies here: **the most specific answer in the chain.**
 *
 * node → type → phase → estate. Set it once on the Property and everything under it uses it;
 * override it on a phase, on a type, or on a single node where that one differs. Null all the way
 * up means no commission, which is what a developer selling their own stock means and what every
 * row written before these columns existed means.
 *
 * The type sits above the phase because a type hangs off a phase — "3-bed terraces pay 3%" is a
 * statement about a narrower set than "Phase 1A pays 2.5%", and the narrower statement is the one
 * somebody meant.
 *
 * Basis points throughout, for the same reason money is minor units: 2.5% is 250, never
 * 0.025000000000000001.
 *
 * The commission *feature* — who is owed it, when it accrues, how it is paid — is somebody else's
 * work. This is the rate, stored and readable, so the tree already carries it on the day it lands.
 */
export function resolveCommissionBp(chain: CommissionChain): number | null {
  for (const level of COMMISSION_ORDER) {
    const bp = chain[level]?.commissionBp;
    if (bp !== null && bp !== undefined) return bp;
  }
  return null;
}

/** Where the rate came from, so a screen can say "inherited from the scheme" rather than imply it was set here. */
export function commissionSource(chain: CommissionChain): (typeof COMMISSION_ORDER)[number] | null {
  for (const level of COMMISSION_ORDER) {
    const bp = chain[level]?.commissionBp;
    if (bp !== null && bp !== undefined) return level;
  }
  return null;
}

/* ------------------------------------------------------------- reservations */

/**
 * Reserving, which is what happens before there is a purchase.
 *
 * A sale does not go from nothing to a signed twenty-four-month plan. Somebody expresses an
 * interest, pays a holding fee, and either comes back with the money or does not — and today the
 * status runs `draft → active` with nothing in between, so the stage that actually decides whether
 * a sale happens is the one the product cannot see.
 *
 * **Everything about it is an option, because developers differ.** The three defaults below are
 * the ones the user chose, and each is settable on the scheme and again on the single reservation:
 *
 * | | Default |
 * |---|---|
 * | Does reserving take it off the market? | **Yes** |
 * | Does the reservation expire on its own? | **No** |
 * | Is the fee refundable? | **No** |
 *
 * **The fee counts toward the purchase price, never on top of it.** A buyer who reserves and then
 * buys has already paid part of what they owe, so on conversion the fee is allocated to the plan's
 * oldest instalment like any other payment — which is what puts it first on the statement, because
 * it genuinely is the first money that arrived. Nothing here does that arithmetic; the figure it
 * is allocated at comes from `reservationFeeAmount` and the allocation is the API's.
 *
 * **The amount is a percentage of the price or a flat figure**, because a developer selling ₦18m
 * plots and one selling ₦400m towers do not think about a reservation the same way. Same
 * convention as `UnitCharge`: minor units when the basis is `fixed`, basis points when `percent`.
 *
 * ## The terms are resolved once and then frozen
 *
 * A reservation stores its own answer to all five questions, resolved from the scheme's defaults at
 * the moment it is taken. It is the same rule as `purchases.price` against `plot_types.price`: what
 * was agreed on the day is what was agreed, and a developer changing the scheme's default next
 * month must not silently rewrite the terms of a reservation somebody is already holding.
 *
 * So on a scheme these columns are **defaults**; on a reservation they are **terms**. That is why
 * `resolveReservationTerms` reads `undefined` as "the scheme decides" and `null` as an answer in
 * its own right — the distinction the whole shape rests on, and the one a screen has to preserve
 * when it sends a partial update.
 */

export const RESERVATION_STATUSES = ["held", "converted", "lapsed"] as const;
export type ReservationStatus = (typeof RESERVATION_STATUSES)[number];

/**
 * What a reservation reads as, including the one state that is derived rather than stored.
 *
 * `expired` is not a status anybody writes: it is what `held` becomes when the day passes, exactly
 * as `overdue` is what an instalment becomes. Storing it would need something to have run, and the
 * thing that runs is a sweep — so a screen read one minute before the sweep would say "held" about
 * a reservation that ran out on Tuesday.
 */
export type ReservationState = ReservationStatus | "expired";

export const RESERVATION_STATE_LABELS: Record<ReservationState, string> = {
  held: "Held",
  expired: "Expired",
  converted: "Converted",
  lapsed: "Lapsed",
};

export const RESERVATION_FEE_BASES = ["fixed", "percent"] as const;
export type ReservationFeeBasis = (typeof RESERVATION_FEE_BASES)[number];

export interface ReservationTerms {
  feeBasis: ReservationFeeBasis;
  /**
   * Minor units when `fixed`, basis points when `percent`. **Null means nobody has said.**
   *
   * Not zero. A default figure would be a number invented in this file and charged to a real
   * buyer, and zero would read as "no fee to pay" — which is a decision somebody is entitled to
   * make, and therefore not something to arrive at by accident.
   */
  feeValue: number | null;
  /** Does reserving take the thing off the market. The default, and what most developers mean. */
  holds: boolean;
  /** Is the fee returned if the reservation comes to nothing. */
  refundable: boolean;
  /**
   * How many days it stands for. **Null means it does not expire**, which is the default.
   *
   * One nullable number rather than a boolean beside a period, because the pair has an illegal
   * state — "expires, in null days" — and something would eventually write it. A screen renders
   * this as a switch and a box; the database has nothing to disagree with itself about.
   */
  days: number | null;
}

export const RESERVATION_DEFAULTS: ReservationTerms = {
  feeBasis: "percent",
  feeValue: null,
  holds: true,
  refundable: false,
  days: null,
};

/**
 * The terms this reservation is taken on: the scheme's answer, unless this one gives its own.
 *
 * **`undefined` is "the scheme decides"; `null` is an answer.** `{ days: null }` means *this*
 * reservation never expires even though the scheme's default is fourteen days, and `{}` means take
 * the scheme's fourteen. Collapsing the two would make it impossible to say the first, which is an
 * ordinary thing to want to say about one buyer.
 */
export function resolveReservationTerms(
  scheme: ReservationTerms,
  override?: Partial<ReservationTerms>,
): ReservationTerms {
  const pick = <K extends keyof ReservationTerms>(key: K): ReservationTerms[K] =>
    override && override[key] !== undefined ? (override[key] as ReservationTerms[K]) : scheme[key];
  return {
    feeBasis: pick("feeBasis") ?? RESERVATION_DEFAULTS.feeBasis,
    feeValue: pick("feeValue"),
    holds: pick("holds") ?? RESERVATION_DEFAULTS.holds,
    refundable: pick("refundable") ?? RESERVATION_DEFAULTS.refundable,
    days: pick("days"),
  };
}

/**
 * What the fee comes to, in minor units.
 *
 * `null` rather than `0` wherever the sum cannot be done — nobody has set an amount, or a
 * percentage has no price behind it. Zero is a fee somebody chose to be nothing; null is nobody
 * having said, and a screen showing "0" for the second is telling a buyer there is nothing to pay.
 *
 * Integer arithmetic throughout: `applyRate` rounds to whole minor units, so a fee is never a
 * fraction of one. ADR-0003.
 */
export function reservationFeeAmount(
  terms: { feeBasis: ReservationFeeBasis; feeValue: number | null },
  price: number | null | undefined,
): number | null {
  if (terms.feeValue === null || terms.feeValue === undefined) return null;
  if (terms.feeBasis === "fixed") return terms.feeValue;
  if (price === null || price === undefined) return null;
  return applyRate(price, terms.feeValue as BasisPoints);
}

/**
 * The day it stops standing, or null because it never does.
 *
 * Counted from the day it was taken, as a day — not as an instant plus an offset, which would have
 * a reservation taken at 23:40 expire a day earlier than one taken at 00:20 on the same terms.
 * ADR-0003.
 */
export function reservationExpiry(reservedOn: PlainDate, days: number | null | undefined): PlainDate | null {
  if (days === null || days === undefined || days <= 0) return null;
  return addDays(reservedOn, days);
}

/**
 * What this reservation reads as today.
 *
 * **Derived, not stored**, for the same reason an instalment's `overdue` is: nothing has to have
 * run for the answer to be right. The sweep that puts the thing back on the market still has to
 * run — that writes rows — but the screen does not wait for it to tell the truth.
 *
 * A reservation that has already been converted or lapsed stays what it is. Reading `expired` off
 * the date alone would put a buyer who has paid back on the market.
 *
 * The last day counts. An offer open "until the 15th" is open on the 15th.
 */
export function reservationState(
  reservation: { status: string; expiresOn?: PlainDate | string | null },
  today: PlainDate,
): ReservationState {
  if (reservation.status !== "held") {
    return (RESERVATION_STATUSES as readonly string[]).includes(reservation.status)
      ? (reservation.status as ReservationStatus)
      : "lapsed";
  }
  const expires = reservation.expiresOn;
  if (expires && (expires as string) < today) return "expired";
  return "held";
}

/** Whether the sweep should end this one and put what it holds back on the market. */
export function reservationHasRunOut(
  reservation: { status: string; expiresOn?: PlainDate | string | null },
  today: PlainDate,
): boolean {
  return reservationState(reservation, today) === "expired";
}

/* ------------------------------------------------------- marketer commission */

/**
 * What a marketer earns on a sale, and **when**.
 *
 * Development is sold by marketers on commission per unit, and until this there was none at all:
 * the letting side had `commissionBp` on a property and the developer side had a rate stored on
 * the tree that nothing read. `resolveCommissionBp()` above answers *how much*; everything below
 * answers *when it is earned* and *what it comes to*, which are the two questions a marketer and a
 * developer actually argue about.
 *
 * ## The four bases, and why collections is the default
 *
 * The user chose the default, and it is the one that matches how the money moves: a developer
 * selling a plot on a four-year plan does not hold four years of commission on the day the papers
 * are signed, and a marketer knows it. So commission accrues **as the buyer pays**, and the three
 * other bases are offered because developers differ:
 *
 * | Basis | Earned |
 * |---|---|
 * | `collections` | a share of every payment, as it is allocated. **The default.** |
 * | `reservation` | in full, the day the thing was reserved |
 * | `first_payment` | in full, the day the first money landed |
 * | `completion` | in full, the day the plan was settled |
 *
 * ## Three properties the arithmetic has, and they are the point
 *
 * 1. **The four bases agree on the total and differ only on the timing.** The full commission is
 *    `applyRate(price, bp)` on all four, and `collections` is capped cumulatively at it. So a
 *    developer switching basis changes when a marketer is paid, never how much — and a plan
 *    carrying late fees does not quietly pay commission on the penalties, which is what an
 *    uncapped share of collections would do. A marketer earns a percentage of **the price**;
 *    collections decide the day.
 *
 * 2. **It is worked out from allocations, never from payments.** The caller hands over one entry
 *    per `payment_allocations` row against this plan's instalments. That is the rule ADR-0099 had
 *    to correct on the landlord statement, which counted money *received* — including the part
 *    held as unallocated credit against nothing — and credited a whole payment to one party. Money
 *    that has arrived and not been applied belongs to nobody yet, so it earns nobody anything.
 *
 * 3. **A cancelled sale earns nothing on the three event bases.** The event that earned it was
 *    undone. On `collections` it keeps whatever is still allocated, because that money genuinely
 *    came in — and a reversal removes its allocation anyway, so nothing here has to remember to.
 */

export const COMMISSION_BASES = ["collections", "reservation", "first_payment", "completion"] as const;
export type CommissionBasis = (typeof COMMISSION_BASES)[number];

export const DEFAULT_COMMISSION_BASIS: CommissionBasis = "collections";

export interface CommissionBasisInfo {
  value: CommissionBasis;
  label: string;
  hint: string;
}

export const COMMISSION_BASIS_INFO: Record<CommissionBasis, CommissionBasisInfo> = {
  collections: {
    value: "collections",
    label: "As the buyer pays",
    hint: "A share of every instalment as it is collected. What most developers can afford.",
  },
  reservation: {
    value: "reservation",
    label: "On reservation",
    hint: "The whole commission the day the buyer reserves.",
  },
  first_payment: {
    value: "first_payment",
    label: "On the first payment",
    hint: "The whole commission the day the first money arrives.",
  },
  completion: {
    value: "completion",
    label: "On completion",
    hint: "The whole commission once the plan is settled in full.",
  },
};

export function isCommissionBasis(value: unknown): value is CommissionBasis {
  return typeof value === "string" && (COMMISSION_BASES as readonly string[]).includes(value);
}

/** One allocation of one payment against this plan. The unit commission accrues on. */
export interface CommissionCollection {
  allocationId: string;
  paymentId: string;
  /** The receipt the buyer holds, so the line is checkable from the other side. */
  receiptNumber: string;
  receivedOn: PlainDate;
  amount: number;
}

/**
 * One sale, with everything needed to decide what it has earned.
 *
 * `commissionBp` and `basis` are the **frozen** terms off the purchase row, never re-resolved from
 * the tree. Re-pricing a type or re-rating a phase moves what the next buyer's marketer earns and
 * touches nothing already sold — the same rule as `purchases.price` against `plot_types.price`.
 */
export interface CommissionSale {
  purchaseId: string;
  reference: string;
  /** What was agreed, in minor units. Not the asking price, which may have moved since. */
  price: number;
  /** Null means nobody set a rate, which is what "no commission" means. */
  commissionBp: number | null;
  basis: CommissionBasis;
  cancelled: boolean;
  /** Whether the plan is settled in full. Decides `completion` and nothing else. */
  settled: boolean;
  /** The day the sale was made. The fallback date for `reservation` on a walk-in sale. */
  soldOn: PlainDate;
  /** The day it was reserved, or null when it came out of no reservation. */
  reservedOn: PlainDate | null;
  buyerName: string;
  estateName: string;
  plotLabel: string | null;
  /** Every allocation against this plan. Reversed and unconfirmed payments are not among them. */
  collections: readonly CommissionCollection[];
}

export interface CommissionLine {
  purchaseId: string;
  purchaseReference: string;
  buyerName: string;
  estateName: string;
  plotLabel: string | null;
  /** Which table `sourceId` is in, so somebody can open the row and check the figure. */
  sourceKind: "allocation" | "sale";
  sourceId: string;
  reference: string;
  date: PlainDate;
  description: string;
  /** What the share was taken of. The allocation on `collections`, the price on the other three. */
  gross: number;
  bp: number;
  /**
   * What was earned on this line.
   *
   * Normally `applyRate(gross, bp)`. On the **last** line of a `collections` sale it can be less,
   * where the running total would otherwise pass the full commission — a plan carrying late fees
   * collects more than its price, and a marketer is owed a share of the price.
   */
  commission: number;
}

export interface CommissionAccrual {
  /** The whole commission this sale will ever earn: `applyRate(price, bp)`. */
  full: number;
  /** What it has earned so far. Never more than `full`. */
  earned: number;
  lines: CommissionLine[];
}

/** The full commission on a sale, which every basis agrees on. */
export function commissionOnSale(price: number, bp: number | null | undefined): number {
  const rate = bp ?? 0;
  if (rate <= 0) return 0;
  return applyRate(Math.max(0, Math.trunc(price)), rate as BasisPoints);
}

/**
 * What one sale has earned its marketer, line by line.
 *
 * Pure: no clock, no database. The caller decides which allocations exist and whether the plan is
 * settled; this decides what that comes to, so the same arithmetic serves the agency's statement,
 * the guard on paying a marketer, and whatever a marketer is eventually shown.
 */
export function accrueCommission(sale: CommissionSale): CommissionAccrual {
  const bp = sale.commissionBp ?? 0;
  const full = commissionOnSale(sale.price, bp);
  if (bp <= 0 || full <= 0) return { full: 0, earned: 0, lines: [] };

  const stamp = (
    over: Pick<CommissionLine, "sourceKind" | "sourceId" | "reference" | "date" | "description" | "gross" | "commission">,
  ): CommissionLine => ({
    purchaseId: sale.purchaseId,
    purchaseReference: sale.reference,
    buyerName: sale.buyerName,
    estateName: sale.estateName,
    plotLabel: sale.plotLabel,
    bp,
    ...over,
  });

  const ordered = [...sale.collections].sort((a, b) =>
    a.receivedOn < b.receivedOn
      ? -1
      : a.receivedOn > b.receivedOn
        ? 1
        : a.allocationId < b.allocationId
          ? -1
          : a.allocationId > b.allocationId
            ? 1
            : 0,
  );

  if (sale.basis === "collections") {
    const lines: CommissionLine[] = [];
    let earned = 0;
    for (const collection of ordered) {
      if (earned >= full) break;
      const share = Math.min(applyRate(Math.max(0, collection.amount), bp as BasisPoints), full - earned);
      if (share <= 0) continue;
      earned += share;
      lines.push(
        stamp({
          sourceKind: "allocation",
          sourceId: collection.allocationId,
          reference: collection.receiptNumber,
          date: collection.receivedOn,
          description: `${sale.buyerName} — ${sale.reference}, receipt ${collection.receiptNumber}`,
          gross: collection.amount,
          commission: share,
        }),
      );
    }
    return { full, earned, lines };
  }

  /* The three event bases. A cancelled sale earns nothing on them: the reservation, the first
     payment or the completion that earned it was undone, and there is no honest line to draw. */
  if (sale.cancelled) return { full, earned: 0, lines: [] };

  if (sale.basis === "reservation") {
    /* A developer on this basis who takes a walk-in sale still owes commission on it. There was no
       reservation stage, so the sale itself is the commitment and the day it was made is the day.
       Refusing to accrue would silently unpay a marketer for the sales they closed fastest. */
    const date = sale.reservedOn ?? sale.soldOn;
    return {
      full,
      earned: full,
      lines: [
        stamp({
          sourceKind: "sale",
          sourceId: sale.purchaseId,
          reference: sale.reference,
          date,
          description: sale.reservedOn
            ? `${sale.buyerName} — ${sale.reference}, reserved`
            : `${sale.buyerName} — ${sale.reference}, sold without a reservation`,
          gross: sale.price,
          commission: full,
        }),
      ],
    };
  }

  if (sale.basis === "first_payment") {
    const first = ordered[0];
    if (!first) return { full, earned: 0, lines: [] };
    return {
      full,
      earned: full,
      lines: [
        stamp({
          sourceKind: "sale",
          sourceId: sale.purchaseId,
          reference: sale.reference,
          date: first.receivedOn,
          description: `${sale.buyerName} — ${sale.reference}, first payment ${first.receiptNumber}`,
          gross: sale.price,
          commission: full,
        }),
      ],
    };
  }

  /* `completion`. Dated by the payment that settled it rather than by today, so the line does not
     move every time somebody opens the statement. */
  if (!sale.settled) return { full, earned: 0, lines: [] };
  const last = ordered[ordered.length - 1];
  return {
    full,
    earned: full,
    lines: [
      stamp({
        sourceKind: "sale",
        sourceId: sale.purchaseId,
        reference: sale.reference,
        date: last ? last.receivedOn : sale.soldOn,
        description: `${sale.buyerName} — ${sale.reference}, settled in full`,
        gross: sale.price,
        commission: full,
      }),
    ],
  };
}

/** One payment the agency has actually made to a marketer. */
export interface MarketerPayment {
  id: string;
  reference: string;
  paidOn: PlainDate;
  amount: number;
}

/**
 * What a marketer has earned, what they have been paid, and what they are owed.
 *
 * **Cumulative, with no period.** That is deliberate, and it is the lesson of ADR-0099: two
 * computations of the same money disagreed by 942m minor units, and what let them was each having
 * its own idea of which rows were in scope. A marketer's account has one honest answer — everything
 * earned, less everything paid — so there is no window to get wrong and no options object for two
 * callers to pass differently. Whoever wants a period slices `lines`.
 */
export interface MarketerStatement {
  marketerId: string;
  lines: CommissionLine[];
  /** What every sale has accrued, summed. */
  earned: number;
  /** What has actually been handed over. Reversed payments are not among them. */
  paid: number;
  /** `earned − paid`. **May be negative** — a marketer paid ahead of a sale that was then cancelled. */
  owed: number;
  payments: MarketerPayment[];
  saleCount: number;
  lineCount: number;
}

export function buildMarketerStatement(input: {
  marketerId: string;
  sales: readonly CommissionSale[];
  payments: readonly MarketerPayment[];
}): MarketerStatement {
  const lines: CommissionLine[] = [];
  for (const sale of input.sales) lines.push(...accrueCommission(sale).lines);

  /* Date then source id. The second half is not decoration: `ORDER BY` a date alone is not a total
     order, and a statement whose lines move between two viewings is a statement nobody believes. */
  lines.sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : a.sourceId < b.sourceId ? -1 : a.sourceId > b.sourceId ? 1 : 0,
  );

  const earned = lines.reduce((sum, line) => sum + line.commission, 0);
  const paid = input.payments.reduce((sum, payment) => sum + payment.amount, 0);

  return {
    marketerId: input.marketerId,
    lines,
    earned,
    paid,
    owed: earned - paid,
    payments: [...input.payments].sort((a, b) =>
      a.paidOn < b.paidOn ? -1 : a.paidOn > b.paidOn ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
    ),
    saleCount: new Set(lines.map((line) => line.purchaseId)).size,
    lineCount: lines.length,
  };
}

export interface MarketerProblem {
  marketerId: string;
  what: string;
  expected: number;
  found: number;
}

/**
 * Prove a marketer statement adds up, rather than trusting that it does.
 *
 * Called before a payment to a marketer is recorded and again by the verification suites — the
 * same shape as `reconcile()` for a landlord payout, and for the same reason: a wrong figure that
 * was never handed over is a bug, and a wrong figure that was handed over is an argument.
 */
export function reconcileMarketer(statement: MarketerStatement): MarketerProblem[] {
  const problems: MarketerProblem[] = [];
  const note = (what: string, expected: number, found: number) => {
    if (expected !== found) problems.push({ marketerId: statement.marketerId, what, expected, found });
  };

  note("earned", statement.lines.reduce((sum, line) => sum + line.commission, 0), statement.earned);
  note("paid", statement.payments.reduce((sum, payment) => sum + payment.amount, 0), statement.paid);
  note("earned less paid against what is owed", statement.earned - statement.paid, statement.owed);
  note("line count", statement.lines.length, statement.lineCount);

  /* No line may earn more than the rate applied to what it is drawn on. A line that breaks this is
     either a rate above 100% or an arithmetic slip, and both produce a demand nobody can explain. */
  for (const line of statement.lines) {
    const most = applyRate(Math.max(0, line.gross), line.bp as BasisPoints);
    if (line.commission > most) {
      problems.push({
        marketerId: statement.marketerId,
        what: `commission on ${line.reference} exceeds the rate applied to what it is drawn on`,
        expected: most,
        found: line.commission,
      });
    }
  }

  return problems;
}
